"use client";

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, RefreshCw, Play, ExternalLink, Film } from "lucide-react";
import { ROUTES } from "@/constants/routes";
import { kkmovieService } from "@/services/kkmovieService";
import { kkphimEpisodeService } from "@/services/kkphimEpisodeService";
import { KKMovie, KKphimEpisode, EpisodeStatusEnum } from "@/types/kkmovie";
import { toast } from "sonner";

const episodeStatusConfig: Record<EpisodeStatusEnum, { label: string; class: string }> = {
  [EpisodeStatusEnum.SUCCESS]: { label: "Hoạt động", class: "bg-green-100 text-green-800" },
  [EpisodeStatusEnum.PENDING]: { label: "Chờ xử lý", class: "bg-yellow-100 text-yellow-800" },
  [EpisodeStatusEnum.FAILED]: { label: "Lỗi", class: "bg-red-100 text-red-800" },
};

export default function KKMovieEpisodesPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [movie, setMovie] = useState<KKMovie | null>(null);
  const [episodes, setEpisodes] = useState<KKphimEpisode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch movie details
      const movieRes = await kkmovieService.getKKMovieById(id);
      if (movieRes.ok && movieRes.data) {
        const responseData = movieRes.data as any;
        const movieData = responseData?.data?.data || responseData?.data || responseData;
        setMovie(movieData as KKMovie);
      }

      // Fetch episodes
      const episodesRes = await kkphimEpisodeService.getEpisodesByMovie(id);
      if (episodesRes.ok && episodesRes.data) {
        const responseData = episodesRes.data as any;
        const episodesData = responseData?.data?.data || responseData?.data || responseData || [];
        setEpisodes(Array.isArray(episodesData) ? episodesData : []);
      }
    } catch (err) {
      setError("Không thể tải dữ liệu");
      toast.error("Lỗi khi tải dữ liệu");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleRefresh = () => {
    fetchData();
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.KKMOVIES)}
              className="hover:cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Quay lại
            </Button>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600">
                Danh sách tập phim
              </h1>
              {movie && (
                <p className="mt-1 text-gray-500">{movie.name}</p>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={handleRefresh} variant="outline" className="flex items-center gap-2 hover:cursor-pointer">
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
              Làm mới
            </Button>
          </div>
        </div>

        {/* Movie Info Card */}
        {movie && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100">
            <div className="flex gap-6">
              {movie.posters?.[0] && (
                <img
                  src={movie.posters[0]}
                  alt={movie.name}
                  className="w-32 h-48 object-cover rounded-lg shadow"
                />
              )}
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900">{movie.name}</h2>
                {movie.origin_name && (
                  <p className="text-gray-500 mt-1">{movie.origin_name}</p>
                )}
                <div className="flex flex-wrap gap-4 mt-4 text-sm">
                  <div>
                    <span className="text-gray-500">Trạng thái:</span>{" "}
                    <span className="font-medium">{movie.status}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Loại:</span>{" "}
                    <span className="font-medium">{movie.type}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Tập:</span>{" "}
                    <span className="font-medium">{movie.current_episode || 0}/{movie.total_episode || '?'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Năm:</span>{" "}
                    <span className="font-medium">{movie.year || '-'}</span>
                  </div>
                </div>
                {movie.description && (
                  <p className="text-gray-600 text-sm mt-4 line-clamp-3">{movie.description}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Episodes Table */}
        <div className="shadow-lg rounded-xl bg-white border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Danh sách tập ({episodes.length})</h2>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Đang tải...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16">
              <p className="text-red-600">{error}</p>
              <Button onClick={handleRefresh} className="mt-4">
                Thử lại
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50/80">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tập</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tên</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Trạng thái</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Servers</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {episodes.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-16">
                        <div className="flex flex-col items-center justify-center text-center">
                          <Film className="h-12 w-12 text-gray-300 mb-3" />
                          <p className="text-gray-600 font-medium">Chưa có tập phim nào</p>
                          <p className="text-gray-400 text-sm mt-1">Tập phim sẽ được thêm khi sync từ KKPhim API</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    episodes
                      .sort((a, b) => a.episode_number - b.episode_number)
                      .map((episode) => (
                        <tr key={episode._id} className="transition-all hover:bg-gray-50/60">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {episode.episode_number}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {episode.name}
                            {episode.filename && (
                              <div className="text-xs text-gray-400 mt-1 truncate max-w-xs">{episode.filename}</div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${episodeStatusConfig[episode.status]?.class || 'bg-gray-100 text-gray-800'}`}>
                              {episodeStatusConfig[episode.status]?.label || episode.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            <div className="flex flex-wrap gap-1">
                              {episode.servers?.map((server, idx) => (
                                <span key={idx} className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                                  {server.server_name}
                                </span>
                              ))}
                              {(!episode.servers || episode.servers.length === 0) && (
                                <span className="text-gray-400">-</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <div className="flex justify-end gap-2">
                              {episode.servers?.[0]?.link_embed && (
                                <a
                                  href={episode.servers[0].link_embed}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center h-8 px-3 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                  <Play className="h-4 w-4 mr-1" />
                                  Xem
                                </a>
                              )}
                              {episode.servers?.[0]?.link_m3u8 && (
                                <a
                                  href={episode.servers[0].link_m3u8}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center h-8 px-3 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
